import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabaseHelpers } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const MarketplaceSelector = ({ selectedMarketplaces, onAdd, onUpdate, onRemove, projectQuantity }) => {
  const { user } = useAuth();
  const [marketplaces, setMarketplaces] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      const { data } = await supabaseHelpers?.getUserMarketplaces(user?.id);
      setMarketplaces(data || []);
    };
    load();
  }, [user]);

  const sourceList = (marketplaces || [])?.filter(mp => mp?.active !== false);
  const filteredList = sourceList?.filter(mp => {
    const term = search?.toLowerCase()?.trim();
    if (!term) return true;
    return (mp?.name || '')?.toLowerCase()?.includes(term) || (mp?.platform_url || '')?.toLowerCase()?.includes(term);
  });
  const available = filteredList?.filter(mp => (mp?.active !== false) && !selectedMarketplaces?.some(s => s?.marketplaceId === mp?.id));
  const totalQty = parseInt(projectQuantity) || 0;
  const allocated = (selectedMarketplaces || [])?.reduce((sum, s) => sum + (parseInt(s?.quantity) || 0), 0);
  const remaining = Math.max(0, totalQty - allocated);

  return (
    <div>
      <div className="flex items-center space-x-3 mb-2">
        <Icon name="ShoppingCart" size={20} className="text-muted-foreground" />
        <h4 className="text-sm font-medium text-foreground">Marketplaces e Transações</h4>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Vai vender em um marketplace? Adicione qual plataforma e quantas peças você irá vender por transação.
      </p>
      <div className={`mb-2 text-xs ${allocated > totalQty ? 'text-destructive' : 'text-muted-foreground'}`}>
        Itens do projeto: {totalQty} • Alocados: {allocated} • Disponíveis: {remaining}
      </div>
      <div className="space-y-3">
        {/* Controls */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <Input
              label="Buscar marketplace"
              placeholder="Digite nome ou URL"
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">A taxa percentual aplica-se ao total; a taxa fixa multiplica pelos itens de cada transação.</p>
        {/* Selected */}
        {selectedMarketplaces?.length > 0 ? (
          <div className="space-y-2">
            {selectedMarketplaces?.map((sel, idx) => {
              const mp = marketplaces?.find(m => m?.id === sel?.marketplaceId);
              if (!mp) return null;
              const allocatedExceptThis = allocated - (parseInt(sel?.quantity) || 0);
              const maxForThis = Math.max(0, totalQty - allocatedExceptThis);
              return (
                <div key={`${sel?.marketplaceId}-${idx}`} className="flex items-center justify-between border-b border-border/50 pb-2">
                  <div>
                    <div className="font-medium text-foreground">{mp?.name}</div>
                    <div className="text-xs text-muted-foreground">{parseFloat(mp?.fee_percent || 0)?.toFixed(1)}% • R$ {parseFloat(mp?.fixed_fee_per_unit || 0)?.toFixed(2)}</div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Input 
                      label="Itens/transação" 
                      type="number" 
                      min="0" 
                      max={maxForThis}
                      value={sel?.quantity || 0} 
                      onChange={(e) => {
                        const raw = parseInt(e?.target?.value) || 0;
                        const nextQty = Math.max(0, Math.min(raw, maxForThis));
                        onUpdate(idx, { quantity: nextQty });
                      }} 
                      className="w-32" 
                    />
                    <Button size="sm" variant="ghost" iconName="X" onClick={() => onRemove(idx)}>
                      Remover
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Nenhum marketplace selecionado</div>
        )}
        {/* Add — substituir dropdown por botões listados */}
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">Adicionar marketplace:</div>
          <div className="flex flex-wrap gap-2">
            {available?.map(mp => (
              <Button key={mp?.id} size="sm" disabled={remaining <= 0} onClick={() => onAdd({ marketplaceId: mp?.id, name: mp?.name, fee_percent: mp?.fee_percent, fixed_fee_per_unit: mp?.fixed_fee_per_unit })}>
                {mp?.name}
              </Button>
            ))}
            {available?.length === 0 && (
              <span className="text-xs text-muted-foreground">Nenhum disponível para adicionar</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceSelector;